import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Calendar } from 'lucide-react';

// The look of the member's own profile (AttendeeProfile) that the public profile
// (publicProfile/PublicProfilePage) shares: the white hero, the light-blue band
// with its grid, photo, name and title line, motivation, the section headings and
// the event cards. Moved here unchanged, so both pages stay one design.

const API = process.env.REACT_APP_API_URL || '';
export const BLUE = '#47ABDB';

export const initials = (fn, ln) => [fn, ln].filter(Boolean).map((w) => w[0]?.toUpperCase()).join('') || '·';

// The Hero/Grey layout the other member screens share (selfUi.js copies the
// same values) - the same avatar, the same photo-over-the-hero overlap, the
// same card style. Below the hero, this page uses a grid (see GreyInner).
export const Page = styled.div`margin:1.5rem 1rem 3rem;position:relative;@media(max-width:640px){margin:0 0 2rem;}`;

export const Hero = styled.div`position:relative;background:#fff;box-shadow:0 25px 50px -12px rgba(0,0,0,.18);`;

// Below 859px, PhotoWrap pulls the avatar up over the Hero by a fixed amount
// (see its own comment) - on desktop the 15rem min-height below keeps plenty
// of clearance under the headline text no matter what, but that min-height
// is dropped on a phone since the headline block would otherwise leave
// awkward empty space. Extra bottom padding (rather than a min-height, which
// could still be too short on a narrow phone where the tagline wraps to an
// extra line) reserves that same clearance regardless of how many lines the
// headline text wraps to - without it the pulled-up avatar overlaps the
// subheadline/tagline text. Verified at 316-390px: a fixed ~40px gap between
// the tagline and the avatar holds across that whole range.
export const HeroInner = styled.div`
  max-width:78rem; margin:0 auto; padding:3.75rem 2.5rem 3.25rem; min-height:15rem;
  @media(max-width:600px){ padding:2.5rem 1.5rem 7.5rem; min-height:0; }
`;

export const BigHeadline = styled.h1`margin:0;color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.02em;line-height:1.04;font-size:clamp(2rem,4.4vw,3.4rem);`;

export const SubHeadline = styled.p`margin:.85rem 0 0;color:#5B5B5B;font-weight:700;font-size:clamp(1rem,1.9vw,1.35rem);`;

// max-width keeps the tagline out of the avatar's overlap zone (the photo is
// pulled up over the hero's bottom - see PhotoWrap) so its last wrapped line
// can't run underneath the profile picture.
export const TagLine = styled.p`margin:.5rem 0 0;color:#5B5B5B;font-weight:400;font-style:italic;font-size:clamp(.85rem,1.3vw,1rem);max-width:40rem;`;

export const Grey = styled.div`background:#d0e6ef;`;

// Named grid areas instead of two flex columns: topLeft (banners +
// motivation) and topRight (photo) share row 1 (an empty speaker row 2
// collapses for a plain attendee), bottomLeft
// (RecommendedStudies) and bottomRight (the history: participations that need
// no managing) share the last row. A CSS grid row is exactly as tall as its
// tallest cell, so that row always starts at the same y in both columns no
// matter how tall topLeft's banners get that day - no more fixed min-height
// calibration needed to keep "HEARTPORT EXPERT" and "HEARTS Conferences"
// aligned. align-items:start keeps the photo pinned to the top of row 1 too,
// so it never drifts down when a banner appears above it.
// $withSpeaker (a person asked to a spot): the speaker area - everything about
// speaking, open invitations included - is row 2 on the left, and the history
// starts right under the photo instead of below the speaker area (it spans rows
// 2 and 3; the last row is the flexible one, so a long history grows that row
// and leaves no gap under the speaker area).
export const GreyInner = styled.div`
  position:relative; max-width:78rem; margin:0 auto; padding:2rem 2.5rem;
  display:grid; align-items:start; column-gap:2.75rem; row-gap:1.75rem;
  grid-template-columns:1fr 27rem;
  grid-template-areas:"topLeft topRight" "speaker topRight" "bottomLeft bottomRight";
  ${p => (p.$withSpeaker ? 'grid-template-areas:"topLeft topRight" "speaker bottomRight" "bottomLeft bottomRight";grid-template-rows:auto auto 1fr;' : '')}
  @media(max-width:859px){
    grid-template-columns:1fr; grid-template-rows:none; gap:1.5rem; padding:1.5rem;
    grid-template-areas:"topRight" "speaker" "bottomRight" "topLeft" "bottomLeft";
  }
`;

export const TopLeft = styled.div`grid-area:topLeft;min-width:0;@media(min-width:860px){padding-top:1.25rem;}`;

export const TopRight = styled.div`grid-area:topRight;`;

export const BottomLeft = styled.div`grid-area:bottomLeft;min-width:0;`;

export const BottomRight = styled.aside`grid-area:bottomRight;`;

export const MotivationLabel = styled.div`
  display:inline-block;font-size:.68rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;
  padding-bottom:6px;background-image:linear-gradient(to right,#4e4094,#14cbde);background-size:100% 2px;background-position:0 bottom;background-repeat:no-repeat;
`;

export const MotivationText = styled.p`font-size:.92rem;font-style:italic;color:#334155;margin:.3rem 0 0;line-height:1.5;max-width:34rem;`;

export const PhotoWrap = styled.div`
  display:flex;flex-direction:column;align-items:center;text-align:center;margin-top:-130px;
  @media(max-width:859px){ margin-top:-104px; }
`;

export const Photo = styled.img`width:190px;height:190px;border-radius:50%;object-fit:cover;border:5px solid ${BLUE};background:#eef2f7;`;

export const Initials = styled.div`width:190px;height:190px;border-radius:50%;border:5px solid ${BLUE};background:#eef2f7;display:flex;align-items:center;justify-content:center;font-size:3.2rem;font-weight:800;color:${BLUE};`;

export const Name = styled.div`margin-top:.75rem;font-weight:800;font-size:1.2rem;color:#111D4E;`;

export const TitleLine = styled.div`color:#5B5B5B;font-size:.9rem;margin-top:.15rem;word-break:break-word;`;

// Same plain headline treatment as RecommendedStudies.js's own section
// title (bold caps + italic subtitle, no background box), so the two
// sections in this two-column layout read as one visual system.
export const HistoryHeader = styled.div`margin:0 0 1rem;`;

export const HistoryTitle = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;`;

export const HistorySubtitle = styled.div`color:#5B5B5B;font-weight:400;font-style:italic;font-size:.92rem;margin-top:.2rem;`;

// $past fades the whole card (thumbnail included) so a glance down the list
// separates what's already happened from what's still upcoming, without
// touching the real outcome status (Confirmed/Declined/...) next to it -
// see PastTag below for that distinction.
export const RegCard = styled.div`display:flex;flex-direction:column;background:#fff;padding:.9rem;margin-bottom:.8rem;box-shadow:0 6px 16px rgba(15,23,42,.06);${p => (p.$past ? 'opacity:.6;' : '')}${p => (p.$highlighted ? `outline:2px solid ${BLUE};outline-offset:2px;` : '')}`;

export const CardTop = styled.div`display:flex;gap:1rem;align-items:center;`;

export const Thumb = styled.img`width:112px;height:82px;object-fit:cover;flex:none;background:#eef2f7;`;

export const ThumbFallback = styled.div`width:112px;height:82px;flex:none;background:#eef2f7;display:flex;align-items:center;justify-content:center;color:#94a3b8;`;

export const RegInfo = styled.div`min-width:0;`;

export const RegName = styled.div`font-weight:700;color:#111D4E;font-size:1.02rem;`;

export const RegMeta = styled.div`color:#5B6B78;font-size:.85rem;margin-top:.12rem;`;

export const RoleTag = styled.span`display:inline-block;padding:.1rem .5rem;font-size:.68rem;font-weight:800;letter-spacing:.03em;text-transform:uppercase;background:#111D4E;color:#fff;margin-right:.4rem;`;

export const ThumbLink = styled(Link)`display:block;flex:none;`;

export const EventNameLink = styled(Link)`color:inherit;text-decoration:none;&:hover{text-decoration:underline;}`;

export const Empty = styled.p`color:#5B6B78;`;

// Event thumbnail with graceful fallback (missing URL or image that fails to load) - the same as hub/SpeakerArea.js's EventThumb.
export const EventThumb = ({ url }) => {
  const [failed, setFailed] = useState(false);
  if (!url || failed) return <ThumbFallback><Calendar size={22} /></ThumbFallback>;
  return <Thumb src={`${API}${url}`} alt="" referrerPolicy="no-referrer" onError={() => setFailed(true)} />;
};
